import { IMentionableCommentPayload } from '@/views/candidate-timeline/models/candidate-profile-response'
import { IResumeParsed } from '@/views/profile-builder/models'
import { IScreeningStage } from '@/views/company-landing/models'
import { CompanyJobTypeEnum } from '@/views/recruiter/models/role-type.enum'
import { ILatestStageEvent } from '@/views/candidate-list/components/latest-activity/latest-stage-events.utils'

export enum LatestActivityEnum {
  CANDIDATE_RESPONSE = 'CANDIDATE_RESPONSE',
  RECRUITER_EMAIL = 'RECRUITER_EMAIL',
  OFFER_SENT = 'OFFER_SENT',
  INTERVIEW_SCORE_ADDED = 'INTERVIEW_SCORE_ADDED',
  STAGE_INVITE_TEMPLATE = 'STAGE_INVITE_TEMPLATE',
  REJECTION_EMAIL = 'REJECTION_EMAIL',
  STAGE_REMINDER_TEMPLATE = 'STAGE_REMINDER_TEMPLATE',
  CANDIDATE_REPLIED = 'CANDIDATE_REPLIED',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  OFFER_REJECTED = 'OFFER_REJECTED',
  CUSTOM_EMAIL = 'CUSTOM_EMAIL'
}

export interface ICalendarRescheduleEvent {
  title: string
  description: string
  interviewers: string[]
  existingInterviewers: string[]
  startDate: string
  endDate: string
  location: string
  address?: string
  organizer: string
  typeOfInterview: string
  scorecardTemplateId: string
  timezone?: string
  recordInterview?: boolean
}

export interface ICalendarUser {
  avatar: string
  calendarUserId: number
  companyId: string
  createdAt: string
  createdBy: string
  deletedAt: string
  deletedBy: string
  email: string
  firstName: string
  id: string
  lastActive: string
  lastName: string
  fullName: string
  phone?: string
  roleId: string
  status: string
  updatedAt: string
  calendarIntegration?: boolean
}

export enum BookingApplicationMappingSourceEnum {
  CAL = 'CAL',
  GOOGLE = 'GOOGLE',
  MICROSOFT = 'MICROSOFT',
}

export enum WcAtsApplicationOrigins {
  APPLIED = 'applied',
  SOURCED = 'sourced',
  AGENCY = 'agency',
  REFERRAL = 'referral',
}

export enum CVFilterTypeEnum {
  INCLUDES_ALL = 'INCLUDES_ALL',
  INCLUDES_ANY = 'INCLUDES_ANY',
}

export enum ExperienceRangeTypeEnum {
  MINIMUM = 'MINIMUM',
  MAXIMUM = 'MAXIMUM',
  RANGE = 'RANGE',
  NONE = 'NONE'
}

export interface ICandidateListBulkActionsQueryPayload {
  jobRoleIds: string
  nameOrEmail: string
  stages: string
  statuses: string
  active: string
  archivalReasonIds: string
  origin: string
  source: string
  owners: string
  tag: string
  unSelectedIds: string
  frequentTags: string
  cvFilterType: CVFilterTypeEnum
  cvSearchItems: string
  linkedinFullUrl?: string
  cvSkills: string
  gender: string
  includeUnspecifiedGender: boolean
  cvPositionEmployer: string
  isCurrentEmployer: boolean
  educationInstitution: string
  educationFieldOfStudy: string
  cvIndustry: string
  cvSpokenLanguages: string
  includeUnspecifiedSpokenLanguages: boolean
  cvKeywords: string
  experienceRangeType: ExperienceRangeTypeEnum
  experienceStartRange: string
  experienceEndRange: string
  nationality: string
  includeUnspecifiedNationality: boolean
  cvScoreMatch: string
  includeUnParsedCVCandidates: boolean
  sourcedBy: string
  referredBy: string
  agency: string
}

export interface CVSearchQueryFilter {
  cvFilterType: CVFilterTypeEnum
  cvSearchItems: string[]
}

export enum JobApplicationCvScoreMatch {
  BEST = 'BEST',
  GOOD = 'GOOD',
  WEAK = 'WEAK',
  UN_SCORED = 'UN_SCORED',
}

export interface ICandidateListQuery {
  jobRoleIds: string[]
  nameOrEmail: string
  stages: string[]
  statuses: string[]
  active: boolean
  archivalReasonIds: string[]
  origin: string[]
  source: string[]
  tag: string[]
  owners: string[]
  frequentTags: string[]
  cvSearch: CVSearchQueryFilter
  unSelectedIds?: string[]
  linkedinFullUrl?: string
  cvSkills: string[]
  gender: string[]
  includeUnspecifiedGender: boolean
  cvPositionEmployer: string[]
  isCurrentEmployer: boolean
  educationInstitution: string[]
  educationFieldOfStudy: string[]
  cvIndustry: string[]
  cvSpokenLanguages: string[]
  includeUnspecifiedSpokenLanguages: boolean
  cvKeywords: string[]
  experienceRangeType: ExperienceRangeTypeEnum
  experienceStartRange: string
  experienceEndRange: string
  nationality: string[]
  includeUnspecifiedNationality: boolean
  cvScoreMatch: string[]
  includeUnParsedCVCandidates: boolean
  sourcedBy: string[]
  referredBy: string[]
  agency: string[]
  applicationFromDate: string
  applicationToDate: string
  lastActivityFromDate: string
  lastActivityToDate: string
  filterId: string
}

export interface ICandidateListInterface {
  candidateFullName: string
  candidateFirstName: string
  candidateLastName: string
  candidateEmail: string
  jobRoleName: string
  stageDisplayName: string
  status: {
    status: string
    color: string
    bgColor: string
  }
  lastActivity: string
  jobRoleId: string
  stageId: string
  applicationId: string
  location: string
  origin: string
  latestStageEvent: ILatestStageEvent
  isSelected: boolean
  allApplications?: number
  source?: string
  tags?: {id: string, tagName: string}[]
  ownerId: string
  isInternalStage: boolean
  jobStatus: string
  jobApplicationStatus?: string
  cvScore: {
    isCriteriaGenerated: boolean
    isCvScoreGenerated: boolean
    match?: JobApplicationCvScoreMatch
  }
  jobType?: CompanyJobTypeEnum
  originAssociatedUserFullName?: string
  agencyName?: string
}

export interface IPageMeta {
  page: number
  take: number
  itemCount: number
  pageCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

export interface ICandidateListResponse {
  data: ICandidateListInterface[]
  meta: IPageMeta
}

export interface ICandidatesActiveCount {
  activeSearchCount: {
    count: number
  }
}

export interface IShortFormJobs {
  id: string
  name: string
  location: string
  status: string
  candidateCount: number
  type: string
  locationId?: string
  departmentId?: string
  department?: string
  ownerId?: string
}

export enum CandidateListFilterTypeEnum {
  SKILLS = 'SKILLS',
  INDUSTRY = 'INDUSTRY',
  LANGUAGES = 'LANGUAGES',
  GENDER = 'GENDER',
  EMPLOYER = 'EMPLOYER',
  EDUCATION_INSTITUTION = 'EDUCATION_INSTITUTION',
  EDUCATION_FIELD_OF_STUDY = 'EDUCATION_FIELD_OF_STUDY',
  KEYWORDS = 'KEYWORDS',
  NATIONALITY = 'NATIONALITY',
}

export const CandidateListFilterMapping: { [key in CandidateListFilterTypeEnum]: keyof ICandidateListQuery} = {
  [CandidateListFilterTypeEnum.SKILLS]: 'cvSkills',
  [CandidateListFilterTypeEnum.GENDER]: 'gender',
  [CandidateListFilterTypeEnum.EDUCATION_FIELD_OF_STUDY]: 'educationFieldOfStudy',
  [CandidateListFilterTypeEnum.EDUCATION_INSTITUTION]: 'educationInstitution',
  [CandidateListFilterTypeEnum.EMPLOYER]: 'cvPositionEmployer',
  [CandidateListFilterTypeEnum.INDUSTRY]: 'cvIndustry',
  [CandidateListFilterTypeEnum.KEYWORDS]: 'cvKeywords',
  [CandidateListFilterTypeEnum.LANGUAGES]: 'cvSpokenLanguages',
  [CandidateListFilterTypeEnum.NATIONALITY]: 'nationality'
}

export interface IInfoCandidateListInterface {
  stages: Array<{
    id: string
    name: string
    count: number
  }>
  statuses: Array<{
    id: string
    name: string
    count: number
  }>
  archivalReasons: Array<{
    id: string
    name: string
    count: number
  }>
  jobs: IShortFormJobs[]
  origin: Array<{
    name: string
    count: number
  }>
  source: Array<{
    name: string
    count: number
  }>
  tags: Array<{
    tagname: string
    tagid: string
    tagcount: string
  }>
  activeSearchCount: {
    count: number
  }
  archivedSearchCount: {
    count: number
  }
  owners: Array<{
    id: string
    count: number
    label?: string
  }>
  frequentTags: Array<{
    tagname: string
    tagid: string
    tagcount: string
  }>
  [CandidateListFilterTypeEnum.SKILLS]: string[]
  [CandidateListFilterTypeEnum.GENDER]: string[]
  [CandidateListFilterTypeEnum.EDUCATION_FIELD_OF_STUDY]: string[]
  [CandidateListFilterTypeEnum.EDUCATION_INSTITUTION]: string[]
  [CandidateListFilterTypeEnum.EMPLOYER]: string[]
  [CandidateListFilterTypeEnum.INDUSTRY]: string[]
  [CandidateListFilterTypeEnum.KEYWORDS]: string[]
  [CandidateListFilterTypeEnum.LANGUAGES]: string[]
  jobMatch: {
    best: number
    good: number
    weak: number
    notScored: number
  }
  sourcedBy: Array<{
    name: string
    id: string
    count: number
  }>
  referredBy: Array<{
    name: string
    id: string
    count: number
  }>
  agency: Array<{
    name: string
    id: string
    count: number
  }>
}

export interface INewCandidate {
  firstName: string
  lastName: string
  email: string
  jobRoleId: string
  stageId: string
  cv: {
    name: string
    id: string
  }
  convertedCv: {
    name: string
    id: string
  }
  resumeThumbnail: {
    url: string
    id: string
  }
  wcAtsOrigin: string
  wcAtsSource: string
  phoneNumber: string
  linkedinUrl: string
  portfolioUrl: string
  referredByUserId?: string
  about?: string
  comments?: string
  tags: string[]
  sourcedByUserId?: string
  agencyId?: string
  commentPayload: IMentionableCommentPayload
}

export interface CVSearchResults {
  active: number
  archive: number
}
export interface IDuplicationApplications {
  id: string
  count: number | undefined
}
export interface ICreateCandidate {
  candidate: INewCandidate
  isCvUploadedOnRightDiv: boolean
  parsedResume: IResumeParsed
  isValid: boolean
  candidateStages: IScreeningStage[]
  isResumeUploaded: boolean
  resumeUrl: string
  isParsed: boolean
  resumeUploading: boolean
  parsingTimeout: boolean
  resumeParsingInProgress: boolean
  isInvitationEnabledForSelectedStage: boolean
  cantGenerateResumeSummary: boolean
  actionPending: boolean
  duplicateApplications: IDuplicationApplications
}
export enum FilterTypesEnum {
  JOBS = 'JOBS',
  TAGS = 'TAGS',
  STATUSES = 'STATUSES',
  ARCHIVAL_REASONS = 'ARCHIVAL_REASONS',
  ORIGIN = 'ORIGIN',
  SOURCE = 'SOURCE',
  OWNERS = 'OWNERS',
  CV_SEARCH_ITEMS = 'CV_SEARCH_ITEMS',
  ALL = 'ALL',
  SOURCED_BY = 'SOURCED_BY',
  REFERRED_BY = 'REFERRED_BY',
  AGENCY = 'AGENCY'
}

export const basicQueryFilters = (): ICandidateListQuery => {
  return {
    nameOrEmail: '',
    jobRoleIds: [],
    stages: [],
    statuses: [],
    active: true,
    archivalReasonIds: [],
    origin: [],
    source: [],
    tag: [],
    owners: [],
    frequentTags: [],
    cvSearch: {
      cvFilterType: CVFilterTypeEnum.INCLUDES_ALL,
      cvSearchItems: []
    },
    unSelectedIds: [],
    cvSkills: [],
    gender: [],
    includeUnspecifiedGender: false,
    cvPositionEmployer: [],
    isCurrentEmployer: false,
    educationInstitution: [],
    educationFieldOfStudy: [],
    cvIndustry: [],
    cvSpokenLanguages: [],
    includeUnspecifiedSpokenLanguages: false,
    cvKeywords: [],
    experienceRangeType: ExperienceRangeTypeEnum.NONE,
    experienceStartRange: '0',
    experienceEndRange: '0',
    nationality: [],
    includeUnspecifiedNationality: false,
    cvScoreMatch: [],
    includeUnParsedCVCandidates: false,
    sourcedBy: [],
    referredBy: [],
    agency: [],
    applicationFromDate: '',
    applicationToDate: '',
    lastActivityFromDate: '',
    lastActivityToDate: '',
    filterId: ''
  }
}

export const cvScoreMatchFilterInfo = (jobMatch: {
  best: number
  good: number
  weak: number
  notScored: number
}) => [
  {
    id: JobApplicationCvScoreMatch.BEST,
    name: 'Best match',
    count: jobMatch.best
  },
  {
    id: JobApplicationCvScoreMatch.GOOD,
    name: 'Good match',
    count: jobMatch.good
  },
  {
    id: JobApplicationCvScoreMatch.WEAK,
    name: 'Weak match',
    count: jobMatch.weak
  },
  {
    id: JobApplicationCvScoreMatch.UN_SCORED,
    name: 'Score not generated',
    count: jobMatch.notScored
  }
]

export const basicCandidateListInfo = (): IInfoCandidateListInterface => {
  return {
    activeSearchCount: {
      count: 0
    },
    archivedSearchCount: {
      count: 0
    },
    jobs: [],
    origin: [],
    source: [],
    tags: [],
    stages: [],
    statuses: [],
    archivalReasons: [],
    owners: [],
    frequentTags: [],
    [CandidateListFilterTypeEnum.SKILLS]: [],
    [CandidateListFilterTypeEnum.GENDER]: ['Male', 'Female', 'Others'],
    [CandidateListFilterTypeEnum.EDUCATION_FIELD_OF_STUDY]: [],
    [CandidateListFilterTypeEnum.EDUCATION_INSTITUTION]: [],
    [CandidateListFilterTypeEnum.EMPLOYER]: [],
    [CandidateListFilterTypeEnum.INDUSTRY]: [],
    [CandidateListFilterTypeEnum.KEYWORDS]: [],
    [CandidateListFilterTypeEnum.LANGUAGES]: [],
    jobMatch: {
      best: 0,
      good: 0,
      weak: 0,
      notScored: 0
    },
    sourcedBy: [],
    referredBy: [],
    agency: []
  }
}

export const advancedFilterQueryFields: Array<keyof ICandidateListQuery> = [...Object.values(CandidateListFilterMapping), 'isCurrentEmployer', 'experienceRangeType', 'experienceStartRange', 'experienceEndRange', 'includeUnspecifiedSpokenLanguages', 'includeUnspecifiedGender', 'includeUnspecifiedNationality', 'includeUnParsedCVCandidates']
export const baseQueryFields: Array<keyof ICandidateListQuery> = ['statuses', 'origin', 'archivalReasonIds', 'source', 'tag', 'owners', 'jobRoleIds', 'cvScoreMatch', 'sourcedBy', 'referredBy', 'agency']
export const candidateListBoolQueryFields = ['active', 'isCurrentEmployer', 'includeUnspecifiedSpokenLanguages', 'includeUnspecifiedGender', 'includeUnspecifiedNationality', 'includeUnParsedCVCandidates']
export const dateFields: Array<keyof ICandidateListQuery> = ['applicationFromDate', 'applicationToDate', 'lastActivityFromDate', 'lastActivityToDate']

export interface IGroupList<T>{
  label: string
  options: T[]
}

export const CvParsingCreditUsage = 0.7
export const CvScoringCreditUsage = 0.3

export enum CandidateListOnDemandFiltersEnum {
  JOB_MATCH = 'JOB_MATCH',
  CANDIDATE_TASK_STATUS = 'CANDIDATE_TASK_STATUS',
  ORIGIN = 'ORIGIN',
  SOURCE = 'SOURCE',
  TAGS = 'TAGS',
  OWNERS = 'OWNERS',
  ACTIVE_COUNT = 'ACTIVE_COUNT',
  ARCHIVE_COUNT = 'ARCHIVE_COUNT',
  ARCHIVAL_REASON = 'ARCHIVAL_REASON',
  STAGES = 'STAGES',
  JOBS = 'JOBS',
  SOURCED_BY = 'SOURCED_BY',
  REFERRED_BY = 'REFERRED_BY',
  AGENCY = 'AGENCY'
}

export interface IGenericUserData {
  id: string
  firstName: string
  lastName?: string
  fullName?: string
  email: string
  roleType: string
  roleId?: string
}

export const getBulkActionsQueryPayload = (query: ICandidateListQuery): ICandidateListBulkActionsQueryPayload => {
  return {
    jobRoleIds: query.jobRoleIds.join(','),
    nameOrEmail: query.nameOrEmail,
    stages: query.stages.join(','),
    statuses: query.statuses.join(','),
    active: query.active ? 'true' : 'false',
    archivalReasonIds: query.archivalReasonIds.join(','),
    origin: query.origin.join(','),
    source: query.source.join(','),
    owners: query.owners.join(','),
    tag: query.tag.join(','),
    unSelectedIds: query.unSelectedIds ? query.unSelectedIds.join(',') : '',
    cvIndustry: query.cvIndustry.join('||'),
    cvPositionEmployer: query.cvPositionEmployer.join('||'),
    cvScoreMatch: query.cvScoreMatch.join('||'),
    cvSkills: query.cvSkills.join('||'),
    cvSpokenLanguages: query.cvSpokenLanguages.join('||'),
    educationFieldOfStudy: query.educationFieldOfStudy.join('||'),
    educationInstitution: query.educationInstitution.join('||'),
    experienceRangeType: query.experienceRangeType,
    experienceStartRange: query.experienceStartRange,
    experienceEndRange: query.experienceEndRange,
    gender: query.gender.join('||'),
    includeUnspecifiedGender: query.includeUnspecifiedGender,
    includeUnspecifiedNationality: query.includeUnspecifiedNationality,
    includeUnspecifiedSpokenLanguages: query.includeUnspecifiedSpokenLanguages,
    cvKeywords: query.cvKeywords.join('||'),
    frequentTags: query.frequentTags.join(','),
    includeUnParsedCVCandidates: query.includeUnParsedCVCandidates,
    isCurrentEmployer: query.isCurrentEmployer,
    nationality: query.nationality.join('||'),
    cvFilterType: query.cvSearch.cvFilterType,
    cvSearchItems: query.cvSearch.cvSearchItems.join(','),
    sourcedBy: query.sourcedBy.join(','),
    referredBy: query.referredBy.join(','),
    agency: query.agency.join(',')
  }
}
